import React, { useState, useEffect } from "react";
import {
  Container, Button, InputGroup,
  Form, Alert, Spinner, Badge, ProgressBar
} from "react-bootstrap";
import { auth, database } from "../Firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  getResumeParrainage, getStatutLabel,
  getMoisOffertsPourParrain, getMoisOffertsPourFilleul
} from '../services/parrainageService';
import '../styles/css/Parrainage.css';

export default function Parrainage() {
  const [parrainageCode, setParrainageCode] = useState("");
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [copied, setCopied]                 = useState(false);
  const [userData, setUserData]             = useState(null);
  const [referralStats, setReferralStats]   = useState({
    totalReferrals: 0, filleuls: [], resume: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setError("Vous devez être connecté pour accéder à votre parrainage");
        setLoading(false);
        return;
      }
      await fetchUserReferralData(user);
    });
    return () => unsubscribe();
  }, []);

  const fetchUserReferralData = async (currentUser) => {
    try {
      setLoading(true);
      const userSnap = await getDoc(doc(database, "users", currentUser.uid));
      if (!userSnap.exists()) { setError("Profil utilisateur non trouvé"); return; }
      const data = userSnap.data();
      if (!data.codeParrainage) {
        setError("Code de parrainage non trouvé. Contactez le support.");
        return;
      }
      setUserData(data);
      setParrainageCode(data.codeParrainage);
      const q    = query(collection(database, "users"), where("parrain", "==", currentUser.uid));
      const snap = await getDocs(q);
      const filleuls = snap.docs.map(d => {
        const fData = d.data();
        return {
          pseudo:       fData.pseudo || "Anonyme",
          dateCreation: fData.dateCreation || null,
          abonnement:   fData.abonnement || "free",
          moisParrain:  getMoisOffertsPourParrain(fData.abonnement),
          moisFilleul:  getMoisOffertsPourFilleul(fData.abonnement),
        };
      });
      setReferralStats({
        totalReferrals:    snap.size,
        filleuls,
        parrainagesDetail: data.parrainagesDetail || [],
        resume:            getResumeParrainage(data),
      });
    } catch (err) {
      console.error("Erreur parrainage:", err);
      setError("Impossible de récupérer vos informations de parrainage");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(parrainageCode);
    } catch {
      const el = document.createElement('textarea');
      el.value = parrainageCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const getAbonnementLabel = (abonnement) => {
    if (abonnement === "spotyflex") return { label: "SPOTYFLEX mensuel", color: "#0d6efd" };
    if (abonnement === "spotyzen")  return { label: "SPOTYZEN annuel",   color: "#07a993" };
    return { label: "Sans abonnement", color: "#6c757d" };
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: '#07a993' }} />
        <p className="mt-3 text-muted">Chargement de vos informations...</p>
      </Container>
    );
  }

  const resume = referralStats.resume;

  return (
    <Container className="py-4 px-3" style={{ maxWidth: '800px' }}>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="nuage-card position-relative mb-4" style={{
        borderRadius: '16px', overflow: 'hidden',
        minHeight: '220px',
      }}>
        <div className="overlay-parrainage d-flex flex-column justify-content-center align-items-center text-center text-light p-4">
          <h2 style={{
            fontSize: 'clamp(1.4rem, 6vw, 2.8rem)',
            fontWeight: 700, marginBottom: '8px',
          }}>
            Parrainez vos proches
          </h2>
          <h3 style={{
            fontSize: 'clamp(0.95rem, 3vw, 1.3rem)',
            fontWeight: 600, color: '#fff',
            margin: '0 auto', maxWidth: '500px',
          }}>
            Gagnez des mois Premium à chaque parrainage
          </h3>
          {/* {!error && resume && (
            <div className="text-white mt-3" style={{ fontSize: 'clamp(0.85rem, 3vw, 1rem)' }}>
              <div>{referralStats.totalReferrals} filleul{referralStats.totalReferrals > 1 ? 's' : ''}</div>
              <div>{resume.moisCycle} mois offerts ce cycle</div>
              {resume.totalEnReserve > 0 && (
                <div>{resume.totalEnReserve} mois en réserve</div>
              )}
            </div>
          )} */}
        </div>
      </div>

      {/* ── Code de parrainage ────────────────────────────────────────────── */}
      <div className="p-3 p-md-4 rounded mb-4" style={{
        background: '#f8f9fa', border: '1px solid #dee2e6',
        borderRadius: '12px',
      }}>
        {error  && <Alert variant="danger"  style={{ fontSize: '0.88rem' }}>{error}</Alert>}
        {copied && <Alert variant="success" style={{ fontSize: '0.88rem' }}>✅ Code copié !</Alert>}

        {!error && (
          <>
            <Form.Label className="fw-bold d-block text-center mb-2" style={{ fontSize: '0.95rem' }}>
              Votre code de parrainage
            </Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                type="text" value={parrainageCode} readOnly
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(1rem, 5vw, 1.4rem)',
                  letterSpacing: '6px', textAlign: 'center',
                  background: '#fff',
                }}
              />
              <Button
                style={{ background: '#07a993', border: 'none', fontWeight: 600, minWidth: '90px' }}
                onClick={copyToClipboard}>
                📋 Copier
              </Button>
            </InputGroup>
            <Form.Text muted className="text-center d-block" style={{ fontSize: '0.8rem' }}>
              Partagez ce code lors de l'inscription de vos proches
            </Form.Text>

            {navigator.share && (
              <Button variant="outline-success" className="w-100 mt-2"
                style={{ padding: '10px', fontWeight: 600 }}
                onClick={() => navigator.share({
                  title: 'SpotyPark — Mon code de parrainage',
                  text:  `Rejoins SpotyPark avec mon code ${parrainageCode} lors de ton inscription !`,
                })}>
                Partager <i className="fa-solid fa-share-nodes ms-1"></i>
              </Button>
            )}
          </>
        )}
      </div>

      {/* ── Dashboard mois offerts ────────────────────────────────────────── */}
      {resume && (
        <div className="mb-4">
          <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>
            🎁 Mois d'abonnements offerts
          </h5>

          {resume.moisCredit > 0 && (
            <Alert variant="primary" style={{ borderLeft: '4px solid #0d6efd', fontSize: '0.88rem' }}>
              <strong>💳 {resume.moisCredit} mois en crédit</strong> vous attendent !<br />
              <span style={{ fontSize: '0.82rem' }}>
                Appliqués automatiquement à votre prochain renouvellement.
              </span>
            </Alert>
          )}

          {resume.moisAttente > 0 && (
            <Alert variant="warning" style={{ borderLeft: '4px solid #ffc107', fontSize: '0.88rem' }}>
              <strong>⏳ {resume.moisAttente} mois en attente</strong><br />
              <span style={{ fontSize: '0.82rem' }}>
                S'activeront dès votre souscription à un abonnement.
              </span>
            </Alert>
          )}

          {/* Jauge cycle */}
          <div className="p-3 rounded mb-3" style={{
            background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '10px',
          }}>
            <div className="d-flex justify-content-between mb-2">
              <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>Mois offerts ce cycle</span>
              <span style={{ fontWeight: 700, color: '#07a993', fontSize: '0.88rem' }}>
                {resume.moisCycle} / {resume.PLAFOND_MOIS_PAR_CYCLE} mois
              </span>
            </div>
            <ProgressBar
              now={(resume.moisCycle / resume.PLAFOND_MOIS_PAR_CYCLE) * 100}
              style={{ height: '10px', borderRadius: '6px' }}
              variant={resume.plafondAtteint ? "danger" : "success"}
            />
            <div style={{
              fontSize: '0.78rem', marginTop: '6px',
              color: resume.plafondAtteint ? '#dc3545' : '#6c757d',
              fontWeight: resume.plafondAtteint ? 600 : 400,
            }}>
              {resume.plafondAtteint
                ? "⚠️ Plafond atteint — surplus mis en crédit pour le cycle suivant."
                : `Encore ${resume.plafondRestant} mois disponibles ce cycle`}
            </div>
          </div>

          {/* KPI cards — 2 colonnes mobile, 4 desktop */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px',
            marginBottom: '16px',
          }}>
            {[
              { label: "Sur l'abonnement en cours",        value: resume.moisCycle,                   unit: 'mois', color: '#198754', bg: '#d1e7dd', icon: '✅' },
              { label: "En crédit abonnement suivant",      value: resume.moisCredit,                  unit: 'mois', color: '#0d6efd', bg: '#e8f0fe', icon: '💳' },
              { label: "En attente (souscription)",         value: resume.moisAttente,                 unit: 'mois', color: '#ffc107', bg: '#fff3cd', icon: '⏳' },
              { label: "Total historique",                  value: userData?.totalMoisOfferts || 0,    unit: 'mois', color: '#07a993', bg: '#e8f8f5', icon: '🏆' },
            ].map(({ label, value, unit, color, bg, icon }) => (
              <div key={label} className="p-3 rounded text-center" style={{
                background: bg, border: `1px solid ${color}33`,
              }}>
                <div style={{ fontSize: '1.3rem' }}>{icon}</div>
                <div style={{ fontSize: '0.72rem', color: '#6c757d', margin: '4px 0' }}>
                  {label}
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>
                  {value}
                  <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '3px' }}>
                    {unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filleuls — cards mobile, tableau desktop ──────────────────────── */}
      {referralStats.totalReferrals > 0 && (
        <div className="mb-4">
          <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>
            👥 Mes filleuls ({referralStats.totalReferrals})
          </h5>

          {/* Mobile — cards */}
          <div className="d-md-none" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {referralStats.filleuls.map((f, i) => {
              const abo = getAbonnementLabel(f.abonnement);
              return (
                <div key={i} className="p-3 rounded" style={{
                  border: '1px solid #dee2e6', background: '#fff',
                  borderRadius: '10px',
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{f.pseudo}</span>
                    <Badge style={{ background: abo.color, fontSize: '0.72rem' }}>
                      {abo.label}
                    </Badge>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6c757d', marginBottom: '8px' }}>
                    Inscrit le {formatDate(f.dateCreation)}
                  </div>
                  <div className="d-flex justify-content-between">
                    <div style={{ fontSize: '0.82rem' }}>
                      <span style={{ color: '#6c757d' }}>Parrain : </span>
                      <span style={{ color: '#198754', fontWeight: 700 }}>
                        {f.moisParrain > 0 ? `+${f.moisParrain} mois` : '—'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.82rem' }}>
                      <span style={{ color: '#6c757d' }}>Filleul : </span>
                      <span style={{ color: '#0d6efd', fontWeight: 700 }}>
                        {f.moisFilleul > 0 ? `+${f.moisFilleul} mois` : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Total mobile */}
            <div className="p-3 rounded d-flex justify-content-between" style={{
              background: '#f8f9fa', border: '1px solid #dee2e6',
              fontWeight: 700, fontSize: '0.88rem',
            }}>
              <span>Total</span>
              <span>
                <span style={{ color: '#198754' }}>
                  +{referralStats.filleuls.reduce((s, f) => s + f.moisParrain, 0)} mois (parrain)
                </span>
                {' · '}
                <span style={{ color: '#0d6efd' }}>
                  +{referralStats.filleuls.reduce((s, f) => s + f.moisFilleul, 0)} mois (filleul)
                </span>
              </span>
            </div>
          </div>

          {/* Desktop — tableau */}
          <div className="d-none d-md-block table-responsive">
            <table className="table table-striped table-bordered align-middle" style={{ fontSize: '0.88rem' }}>
              <thead style={{ backgroundColor: '#07a993', color: '#fff' }}>
                <tr>
                  <th>Pseudo</th>
                  <th>Date d'inscription</th>
                  <th>Abonnement</th>
                  <th className="text-center">Mois parrain</th>
                  <th className="text-center">Mois filleul</th>
                </tr>
              </thead>
              <tbody>
                {referralStats.filleuls.map((f, i) => {
                  const abo = getAbonnementLabel(f.abonnement);
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{f.pseudo}</td>
                      <td>{formatDate(f.dateCreation)}</td>
                      <td>
                        <Badge style={{ background: abo.color, fontSize: '0.75rem' }}>
                          {abo.label}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {f.moisParrain > 0
                          ? <span style={{ color: '#198754', fontWeight: 700 }}>+{f.moisParrain} mois</span>
                          : <span style={{ color: '#6c757d' }}>—</span>}
                      </td>
                      <td className="text-center">
                        {f.moisFilleul > 0
                          ? <span style={{ color: '#0d6efd', fontWeight: 700 }}>+{f.moisFilleul} mois</span>
                          : <span style={{ color: '#6c757d' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot style={{ backgroundColor: '#f8f9fa', fontWeight: 700 }}>
                <tr>
                  <td colSpan="3">Total</td>
                  <td className="text-center" style={{ color: '#198754' }}>
                    +{referralStats.filleuls.reduce((s, f) => s + f.moisParrain, 0)} mois
                  </td>
                  <td className="text-center" style={{ color: '#0d6efd' }}>
                    +{referralStats.filleuls.reduce((s, f) => s + f.moisFilleul, 0)} mois
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Historique détaillé ───────────────────────────────────────────── */}
      {referralStats.parrainagesDetail?.length > 0 && (
        <div className="mb-4">
          <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>
            📋 Historique détaillé
          </h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {referralStats.parrainagesDetail.map((p, i) => {
              const statutInfo = getStatutLabel(p.statut);
              const aboLabel   = getAbonnementLabel(p.abonnementFilleul);
              return (
                <div key={i} className="p-3 rounded" style={{
                  border:     `1px solid ${statutInfo.color}44`,
                  background: statutInfo.bg,
                  borderLeft: `4px solid ${statutInfo.color}`,
                }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>
                        👤 {p.filleulPseudo}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>
                        {formatDate(p.dateParrainage)} ·{' '}
                        <Badge style={{ background: aboLabel.color, fontSize: '0.68rem' }}>
                          {aboLabel.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-end">
                      <div style={{ fontWeight: 700, color: statutInfo.color, fontSize: '0.95rem' }}>
                        +{p.moisOfferts} mois offerts
                      </div>
                      {p.moisAppliques > 0 && p.moisEnCredit > 0 && (
                        <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                          {p.moisAppliques} appliqués · {p.moisEnCredit} en crédit
                        </div>
                      )}
                      <Badge style={{ background: statutInfo.color, fontSize: '0.68rem', marginTop: '4px' }}>
                        {statutInfo.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Comment ça marche ─────────────────────────────────────────────── */}
      <h3 className="text-center fw-bold mt-4 mb-4" style={{
        fontSize: 'clamp(1.1rem, 4vw, 1.5rem)',
      }}>
        Comment ça marche ?
      </h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      }}>
        {[
          {
            icon: 'bi bi-mailbox-flag',
            titre: '1. Partagez votre code',
            texte: 'Envoyez votre code à vos proches par SMS, email ou réseaux sociaux.',
          },
          {
            icon: 'bi bi-pen',
            titre: "2. Votre ami s'inscrit",
            texte: `Il entre votre code lors de son inscription et vous recevez automatiquement 250 points.`,
          },
          {
            icon: 'bi bi-piggy-bank',
            titre: '3. Gagnez des mois Premium',
            texte: "Dès que votre filleul souscrit à un abonnement, vous gagnez des mois Premium.",
          },
        ].map(({ icon, titre, texte }) => (
          <div key={titre} className="text-center p-3 rounded" style={{
            background: '#fff', border: '1px solid #dee2e6', borderRadius: '10px',
          }}>
            <i className={`${icon} fs-1`} style={{ color: 'rgb(37, 190, 136)' }}></i>
            <h6 className="fw-bold mt-3 mb-2" style={{ fontSize: '0.92rem' }}>{titre}</h6>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{texte}</p>
          </div>
        ))}
      </div>

      {/* ── Tableau récompenses ───────────────────────────────────────────── */}
      <div className="p-3 p-md-4 rounded mb-5" style={{
        background: '#e8f8f5', border: '1px solid #07a993', borderRadius: '12px',
      }}>
        <h6 className="fw-bold text-center mb-3" style={{ color: '#07a993', fontSize: '0.95rem' }}>
          Récapitulatif des récompenses
        </h6>

        {/* Mobile — cards */}
        <div className="d-md-none" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {[
            { condition: "Inscription du filleul",          parrain: "+250 pts",     filleul: "+250 pts",     pColor: '#198754', fColor: '#198754' },
            { condition: "Abonnement mensuel du filleul",   parrain: "1 mois offert",filleul: "—",            pColor: '#0d6efd', fColor: '#6c757d' },
            { condition: "Abonnement annuel du filleul",    parrain: "3 mois offerts",filleul: "1 mois offert",pColor: '#D4C362', fColor: '#D4C362' },
          ].map(({ condition, parrain, filleul, pColor, fColor }) => (
            <div key={condition} className="p-3 rounded" style={{
              background: '#fff', border: '1px solid #b2dfdb', borderRadius: '8px',
            }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>
                {condition}
              </div>
              <div className="d-flex justify-content-between">
                <div style={{ fontSize: '0.82rem' }}>
                  <span style={{ color: '#6c757d' }}>Parrain : </span>
                  <span style={{ fontWeight: 700, color: pColor }}>{parrain}</span>
                </div>
                <div style={{ fontSize: '0.82rem' }}>
                  <span style={{ color: '#6c757d' }}>Filleul : </span>
                  <span style={{ fontWeight: 700, color: fColor }}>{filleul}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop — tableau */}
        <div className="d-none d-md-block">
          <table className="table table-sm table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ fontSize: '0.88rem' }}>Conditions de récompenses</th>
                <th className="text-center" style={{ fontSize: '0.88rem' }}>Parrain</th>
                <th className="text-center" style={{ fontSize: '0.88rem' }}>Filleul</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '0.88rem' }}>
              <tr>
                <td>Inscription du filleul</td>
                <td className="text-center text-success fw-bold">+250 pts</td>
                <td className="text-center text-success fw-bold">+250 pts</td>
              </tr>
              <tr>
                <td>Abonnement mensuel du filleul</td>
                <td className="text-center text-primary fw-bold">1 mois offert</td>
                <td className="text-center text-muted">—</td>
              </tr>
              <tr>
                <td>Abonnement annuel du filleul</td>
                <td className="text-center fw-bold" style={{ color: "#D4C362" }}>3 mois offerts</td>
                <td className="text-center fw-bold" style={{ color: "#D4C362" }}>1 mois offert</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="text-muted text-center mt-2" style={{ fontSize: '0.75rem' }}>
          * Offres valables pour tout nouveau filleul
        </div>

        {/* Règles cumul */}
        <div className="mt-3 p-3 rounded" style={{
          background: '#fff3cd', border: '1px solid #ffc107',
          fontSize: '0.85rem', color: '#856404', borderRadius: '8px',
        }}>
          <strong>📋 Règles de cumul :</strong>
          <ul className="mb-0 mt-2" style={{ paddingLeft: '18px' }}>
            <li>Les mois offerts sont <strong>cumulatifs</strong>.</li>
            <li>Maximum <strong>12 mois offerts</strong> par année d'abonnement.</li>
            <li>Le surplus est automatiquement mis en crédit pour l'année suivante sous condition de renouvellement.</li>
          </ul>
        </div>
      </div>

    </Container>
  );
}